import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sendContactEmail } from "../lib/emailjs";

interface HeroFormProps {
  title: string;
  description: string;
}

function HeroForm({ title, description }: HeroFormProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "", // Add this line
    category: "",
    description: "",
  });

  const handleBlur = () => {
    const hasValue = Object.values(formData).some((val) => val.trim() !== "");
    if (!hasValue) {
      setIsExpanded(false);
    }
  };

  const isFormValid = () => {
    return Object.values(formData).every((value) => value.trim() !== "");
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const hasValue = Object.values({ ...formData, [name]: value }).some(
      (val) => val !== ""
    );
    setIsExpanded(hasValue);
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      const result = await sendContactEmail(formData);
      if (result?.success) {
        setFormData({
          name: "",
          email: "",
          phone: "",
          category: "",
          description: "",
        });
        setIsExpanded(false);
        navigate("/bedankt-offerte");
      } else {
        setSubmitStatus({
          success: false,
          message:
            result.error ||
            "Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.",
        });
      }
    } catch (error) {
      setSubmitStatus({
        success: false,
        message:
          "Er is iets misgegaan. Probeer het later opnieuw of neem telefonisch contact op.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white w-full relative border border-solid border-shade100 rounded-xl max-w-[448px] overflow-visible py-7 px-5 md:py-10 md:px-8"
      id="offer"
    >
      <form onSubmit={handleSubmit}>
        <div>
          <h1 className="font-bold text-black leading-[1.25] text-[28px] lg:text-[36px] text-[#17354A] block mb-4 leading-[35px] text-[2rem] md:inline-block md:mb-5 md:text-[44px] md:leading-[42px] lg:block">
            {title}
          </h1>
          <div className="text-black leading-[1.56] text-[16px] md:text-[18px] font-normal text-[#17354A] mb-6 md:mb-8">
            {description}
          </div>
        </div>

        <div className="grid grid-cols-12 grid-rows-[repeat(3,_auto)] gap-x-3">
          <div className="col-span-12 transition-all duration-300">
            <div className="mb-4">
              <div className="relative">
                <input
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  Naam
                </label>
              </div>
            </div>
          </div>

          <div
            className={`col-span-12 transition-all duration-300 ${
              isExpanded
                ? "opacity-100 max-h-24"
                : "opacity-0 max-h-0 overflow-hidden"
            }`}
          >
            <div className="mb-4">
              <div className="relative">
                <input
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  Telefoonnummer
                </label>
              </div>
            </div>
          </div>

          <div
            className={`col-span-12 transition-all duration-300 ${
              isExpanded
                ? "opacity-100 max-h-24"
                : "opacity-0 max-h-0 overflow-hidden"
            }`}
          >
            <div className="mb-4">
              <div className="relative">
                <input
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  E-mailadres
                </label>
              </div>
            </div>
          </div>

          <div
            className={`col-span-12 transition-all duration-300 ${
              isExpanded
                ? "opacity-100 max-h-24"
                : "opacity-0 max-h-0 overflow-hidden"
            }`}
          >
            <div className="mb-4">
              <div className="relative">
                <select
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  <option value="">-</option>
                  <option value="renovatie">Renovatie</option>
                  <option value="groepenkast">Groepenkast</option>
                  <option value="elektra">Elektra aanleggen</option>
                  <option value="laadpaal">Laadpaal installatie</option>
                  <option value="zonnepanelen">Zonnepanelen installatie</option>
                  <option value="overig">Overig</option>
                </select>
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  Categorie
                </label>
              </div>
            </div>
          </div>

          <div
            className={`col-span-12 transition-all duration-300 ${
              isExpanded
                ? "opacity-100 max-h-40"
                : "opacity-0 max-h-0 overflow-hidden"
            }`}
          >
            <div className="mb-4">
              <div className="relative">
                <textarea
                  className="block w-full bg-white text-black appearance-none text-base outline-none border-solid rounded-lg border border-[#E5E7EB] transition-all shadow-none duration-150 hover:border-[#17354A]/50 focus:border-[#17354A] box-border h-14 pt-4 px-4 placeholder:opacity-0"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  style={{ height: "120px", resize: "none" }}
                />
                <label className="absolute top-2 origin-left pointer-events-none left-4 pr-4 transition-all duration-150 text-gray-500 text-sm">
                  Beschrijving
                </label>
              </div>
            </div>
          </div>

          <div className="col-span-12 transition-all duration-300">
            <button
              className={`relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] text-base px-5 h-12 w-full ${
                isFormValid() && !isSubmitting
                  ? "bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80"
                  : "bg-[#F47B20]/50 text-white cursor-not-allowed"
              }`}
              type="submit"
              disabled={!isFormValid() || isSubmitting}
            >
              <span className="flex items-center gap-1">
                {isSubmitting ? "Verzenden..." : "Terugbelverzoek"}
                <ChevronRight className="w-4 h-4" />
              </span>
            </button>
            {submitStatus.message && (
              <div
                className={`mt-4 p-4 rounded-lg text-center ${
                  submitStatus.success
                    ? "bg-green-50 text-green-800"
                    : "bg-red-50 text-red-800"
                }`}
              >
                {submitStatus.message}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default HeroForm;
